import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { auth, setToken } from "../lib/api";
import { toast } from "sonner";
import { LogIn, Sparkles } from "lucide-react";
import { GlitchLogo } from "../components/GlitchLogo";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const data = await auth.login(email, password);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen w-full flex items-center justify-center px-6 overflow-hidden"
    >
      {/* Ambient orb backdrop */}
      <motion.div
        className="absolute -z-10 rounded-full"
        style={{
          width: 600,
          height: 600,
          background:
            "radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(59,130,246,0.05) 55%, transparent 75%)",
          filter: "blur(60px)",
        }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md glass-strong rounded-3xl p-10 relative z-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <GlitchLogo size={56} />
          <div>
            <div className="font-display text-3xl font-semibold tracking-tight">
              Glitch
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-blue-300/70 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              Your personal voice AI
            </div>
          </div>
        </div>

        <h1 className="font-display text-2xl mb-1">Welcome back.</h1>
        <p className="text-sm text-white/50 mb-8">
          Sign in to keep the conversation going.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-black/30 border-white/10 h-11 focus-visible:ring-blue-500/50"
              placeholder="you@yourself.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/60">
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-black/30 border-white/10 h-11 focus-visible:ring-blue-500/50"
              placeholder="••••••••"
            />
          </div>
          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-600/30"
          >
            {loading ? (
              "Signing in…"
            ) : (
              <>
                Login using Email <LogIn className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/30">
          <div className="flex-1 h-px bg-white/10" />
          or
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <Button
          data-testid="google-signin-btn"
          type="button"
          variant="outline"
          onClick={() => {
            const redirectUrl = window.location.origin + "/";
            window.location.href =
              "https://auth.emergentagent.com/?redirect=" +
              encodeURIComponent(redirectUrl);
          }}
          className="w-full h-11 rounded-full bg-white/5 hover:bg-white/10 border-white/15 text-white font-medium"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2" aria-hidden="true">
            <path
              fill="#EA4335"
              d="M12 10.2v3.9h5.5c-.24 1.44-1.72 4.23-5.5 4.23-3.31 0-6.01-2.74-6.01-6.13S8.69 6.07 12 6.07c1.88 0 3.14.8 3.86 1.49l2.63-2.53C16.98 3.66 14.7 2.5 12 2.5 6.98 2.5 2.9 6.58 2.9 11.6S6.98 20.7 12 20.7c6.94 0 8.6-4.86 8.6-8.4 0-.56-.06-.99-.14-1.42H12z"
            />
          </svg>
          Continue with Google
        </Button>

        <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-white/30 text-center">
          Single user · private · encrypted session
        </div>
      </motion.div>
    </div>
  );
}
